import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useIntl } from 'react-intl';
import { LanguageContext } from '../App';

type Locale = 'en' | 'zh';

const Navbar: React.FC = () => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const { setLocale } = React.useContext(LanguageContext);
  const intl = useIntl();

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleLanguageChange = (newLocale: Locale) => {
    setLocale(newLocale);
    handleCloseNavMenu();
  };

  const pages = [
    { name: intl.formatMessage({ id: 'navbar.home' }), path: '/' },
    {
      name: intl.formatMessage({ id: 'navbar.usGreenCard' }),
      path: '/us-green-card',
      subpages: [
        { name: 'EB-1A & NIW', path: '/us-green-card/eb1a-niw' },
        { name: 'EB-1C', path: '/us-green-card/eb1c' },
        { name: 'EB-5', path: '/us-green-card/eb5' },
      ],
    },
    {
      name: intl.formatMessage({ id: 'navbar.citizenship' }),
      path: '/citizenship',
      subpages: [
        { name: intl.formatMessage({ id: 'nav.spain' }), path: '/citizenship/spain' },
        { name: intl.formatMessage({ id: 'nav.belgium' }), path: '/citizenship/belgium' },
        { name: intl.formatMessage({ id: 'nav.france' }), path: '/citizenship/france' },
        { name: intl.formatMessage({ id: 'nav.mexico' }), path: '/citizenship/mexico' },
        { name: intl.formatMessage({ id: 'nav.argentina' }), path: '/citizenship/argentina' },
        { name: intl.formatMessage({ id: 'nav.slovenia' }), path: '/citizenship/slovenia' },
      ],
    },
    {
      name: intl.formatMessage({ id: 'navbar.residencyPrograms' }),
      path: '/other-citizenship',
      subpages: [
        { name: intl.formatMessage({ id: 'nav.croatia' }), path: '/other-citizenship/croatia' },
        { name: intl.formatMessage({ id: 'nav.turkey' }), path: '/other-citizenship/turkey' },
        { name: intl.formatMessage({ id: 'nav.stLucia' }), path: '/citizenship/st-lucia' },
        { name: intl.formatMessage({ id: 'nav.greece' }), path: '/other-citizenship/greece' },
        { name: intl.formatMessage({ id: 'nav.japan' }), path: '/other-citizenship/japan' },
        { name: intl.formatMessage({ id: 'nav.malta' }), path: '/other-citizenship/malta' },
      ],
    },
    { name: intl.formatMessage({ id: 'navbar.other' }), path: '/other-programs' },
    { name: intl.formatMessage({ id: 'navbar.contact' }), path: '/contact' },
    { name: intl.formatMessage({ id: 'navbar.about' }), path: '/about' },
  ];

  return (
    <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 'none' }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box
            component="img"
            src="/logo.png"
            alt="Newborn LLC"
            sx={{
              height: 60,
              mr: 2,
              display: { xs: 'none', md: 'flex' },
            }}
          />

          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{ color: '#333' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page) => (
                <div key={page.path}>
                  <MenuItem
                    component={RouterLink}
                    to={page.path}
                    onClick={handleCloseNavMenu}
                  >
                    <Typography textAlign="center" color="text.primary">{page.name}</Typography>
                  </MenuItem>
                  {page.subpages?.map((subpage) => (
                    <MenuItem
                      key={subpage.path}
                      component={RouterLink}
                      to={subpage.path}
                      onClick={handleCloseNavMenu}
                      sx={{ pl: 4 }}
                    >
                      <Typography textAlign="center" color="text.primary">{subpage.name}</Typography>
                    </MenuItem>
                  ))}
                </div>
              ))}
              <MenuItem onClick={() => handleLanguageChange('en')}>
                <Typography textAlign="center" color="text.primary">English</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleLanguageChange('zh')}>
                <Typography textAlign="center" color="text.primary">中文</Typography>
              </MenuItem>
            </Menu>
          </Box>

          <Box
            component="img"
            src="/logo.png"
            alt="Newborn LLC"
            sx={{
              height: 60,
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
            }}
          />

          <Box 
            sx={{ 
              flexGrow: 1, 
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              gap: 2
            }}
          >
            {pages.map((page) => (
              <div key={page.path}>
                <Button
                  component={RouterLink}
                  to={page.path}
                  onClick={handleCloseNavMenu}
                  sx={{ 
                    my: 2, 
                    color: '#333', 
                    display: 'block',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  {page.name}
                </Button>
                {page.subpages && (
                  <Box
                    sx={{
                      position: 'absolute',
                      backgroundColor: 'white',
                      boxShadow: 1,
                      borderRadius: 1,
                      display: 'none',
                      '&:hover': {
                        display: 'block',
                      },
                    }}
                  >
                    {page.subpages.map((subpage) => (
                      <Button
                        key={subpage.path}
                        component={RouterLink}
                        to={subpage.path}
                        onClick={handleCloseNavMenu}
                        sx={{ 
                          color: '#333', 
                          display: 'block',
                          width: '100%',
                          justifyContent: 'flex-start',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          }
                        }}
                      >
                        {subpage.name}
                      </Button>
                    ))}
                  </Box>
                )}
              </div>
            ))}
          </Box>

          <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}>
            <Button
              onClick={() => handleLanguageChange('en')}
              sx={{ 
                color: '#333',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              English
            </Button>
            <Button
              onClick={() => handleLanguageChange('zh')}
              sx={{ 
                color: '#333',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              中文
            </Button>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar; 